import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { EventType } from '@lib/models/enums';
import { InteractionPartUtils } from '@lib/models/InteractionPart';
import type { PartRendererProps } from './types';
import { useAiChatServices } from '../../AiChatContext';
import { useTheme } from '@mui/material/styles';

/**
 * Renders an `ask_user` tool call. Two faces:
 *  - INTERACTIVE (unanswered) — the questionnaire form, shown live in the
 *    pane-level AskUserSheet: `single` => buttons, `multi` => checkboxes,
 *    no-options => free-text, always an optional note (doubles as the "none of
 *    the above" escape). One single-select question with no note submits on a
 *    single click; touch the note and a Submit button appears.
 *  - LOCKED (answered / reload) — a pure Question -> Answer summary (no
 *    controls), rendered inline in the transcript.
 *
 * Submitting sends the answers back as a JSON string over the WebSocket
 * (ChatAction.AnswerHumanTool, keyed by the tool-call id), unblocking the parked
 * server-side tool so the turn continues.
 */

type SelectMode = 'single' | 'multi';
interface AskQuestion {
    id?: string;
    text: string;
    select: SelectMode;
    options: string[];
}
interface Questionnaire {
    title?: string;
    questions: AskQuestion[];
}
interface QAnswer {
    questionId: string;
    selected: string[];
    note?: string;
}
interface QState {
    selected: string[];
    note: string;
}

const asObj = (v: unknown): Record<string, unknown> | null =>
    v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
const asStr = (v: unknown): string => (typeof v === 'string' ? v : '');

/** Coerce an already-parsed value into a normalised Questionnaire, or null if unusable. */
function coerceQuestionnaire(raw: unknown): Questionnaire | null {
    const obj = asObj(raw);
    if (!obj) return null;
    const rawQs = obj.questions;
    if (!Array.isArray(rawQs) || rawQs.length === 0) return null;

    const questions: AskQuestion[] = [];
    for (const item of rawQs) {
        const q = asObj(item);
        if (!q) continue;
        const text = asStr(q.text).trim();
        if (!text) continue;
        const select: SelectMode = asStr(q.select).toLowerCase() === 'multi' ? 'multi' : 'single';
        const options = (Array.isArray(q.options) ? q.options : [])
            .filter((o): o is string => typeof o === 'string' && o.trim().length > 0)
            .map((o) => o.trim());
        const id = asStr(q.id).trim() || undefined;
        questions.push({ id, text, select, options });
    }
    if (questions.length === 0) return null;

    const title = asStr(obj.title).trim() || undefined;
    return { title, questions };
}

/** Read the questionnaire from the tool-call args — new `questionnaire` JSON, or legacy `question`. */
function readQuestionnaire(args: unknown): Questionnaire | null {
    const a = asObj(args);
    if (!a) return null;

    if (typeof a.questionnaire === 'string' && a.questionnaire.trim()) {
        try {
            const parsed = coerceQuestionnaire(JSON.parse(a.questionnaire));
            if (parsed) return parsed;
        } catch {
            /* fall through */
        }
    }
    const fromObj = coerceQuestionnaire(a.questionnaire);
    if (fromObj) return fromObj;

    if (typeof a.question === 'string' && a.question.trim()) {
        return { questions: [{ id: 'q0', text: a.question.trim(), select: 'single', options: ['Yes', 'No'] }] };
    }
    return null;
}

/**
 * Deep-parse the tool's echoed answer into QAnswer[]. The response can arrive
 * char-indexed (persisted shape) or JSON-string-encoded one — or even two —
 * levels deep, because the transport re-encodes the tool's already-JSON string
 * result. Returns the answers array, or null if it's a plain sentinel string
 * (timeout / dismissed) or otherwise unparseable.
 */
function parseAnswers(raw: unknown): QAnswer[] | null {
    let v: unknown = raw;
    if (v && typeof v === 'object') {
        const collapsed = InteractionPartUtils.tryCollapseCharIndexedObject(v);
        if (typeof collapsed === 'string' && collapsed.trim()) v = collapsed;
    }
    for (let i = 0; i < 3 && typeof v === 'string'; i++) {
        const t = v.trim();
        if (!(t.startsWith('{') || t.startsWith('[') || t.startsWith('"'))) break;
        try {
            v = JSON.parse(t);
        } catch {
            break;
        }
    }
    const obj = asObj(v);
    return obj && Array.isArray(obj.answers) ? (obj.answers as QAnswer[]) : null;
}

/** A plain sentinel (non-JSON) answer string — e.g. "(the user did not answer in time)". */
function sentinelOf(raw: unknown): string | null {
    if (typeof raw !== 'string') return null;
    const t = raw.trim();
    if (!t || t.startsWith('{') || t.startsWith('[') || t.startsWith('"')) return null;
    return t;
}

// Only `part` is required; the rest of PartRendererProps is optional so the
// pane-level AskUserSheet can render this with just the parked part + `embedded`.
// Still assignable to the PartRenderer registry, which passes the full props.
type AskUserPartProps = Partial<PartRendererProps> & { part: PartRendererProps['part']; embedded?: boolean };

const AskUserPart: React.FC<AskUserPartProps> = ({ part, embedded = false }) => {
    const { agentGatewayService } = useAiChatServices();
    const [submitted, setSubmitted] = useState<string | null>(null);
    const [answers, setAnswers] = useState<Record<number, QState>>({});

    const theme = useTheme();

    const callId = part.functionDetails?.id;
    const questionnaire = useMemo(
        () => readQuestionnaire(part.functionDetails?.args),
        [part.functionDetails?.args],
    );

    // Answered = submitted this session, OR the function response already carries
    // the answer (reload / late render). Keep the response RAW — parseAnswers
    // unwraps whatever encoding it arrives in.
    const serverAnswer = part.eventType === EventType.FunctionResponse
        ? (part.functionDetails?.response ?? null)
        : null;
    const effective: unknown = submitted ?? serverAnswer;
    const locked = effective != null || !callId || !questionnaire;

    if (!questionnaire) return null;
    const { title, questions } = questionnaire;
    const qid = (i: number) => questions[i].id || String(i);

    const outerSx = embedded
        ? { maxWidth: '100%' }
        : { border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2, py: 1.5, my: 0.5, maxWidth: '100%' };

    // ── Locked: pure Question -> Answer summary (no interactive controls) ──────
    if (locked) {
        const parsed = parseAnswers(effective);
        const sentinel = parsed ? null : sentinelOf(effective);
        return (
            <Box sx={outerSx}>
                {title && (
                    <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 600 }}>{title}</Typography>
                )}
                <Stack spacing={1.25}>
                    {questions.map((q, i) => {
                        const a = parsed?.find((x) => x.questionId === qid(i)) ?? null;
                        const sel = a?.selected ?? [];
                        const note = (a?.note ?? '').trim();
                        const primary = sel.length ? sel.join(', ') : (note || '—');
                        const extraNote = sel.length && note ? note : '';
                        const answered = sel.length > 0 || (q.options.length === 0 && !!note);
                        return (
                            <Box key={qid(i)}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{q.text}</Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ mt: 0.25, color: answered ? 'text.primary' : 'text.secondary' }}
                                >
                                    {primary}
                                </Typography>
                                {extraNote && (
                                    <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                        “{extraNote}”
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    {sentinel ?? '✓ answered'}
                </Typography>
            </Box>
        );
    }

    // ── Interactive: the live questionnaire form (shown in the sheet) ──────────
    const getState = (i: number): QState => answers[i] ?? { selected: [], note: '' };
    const selectedFor = (i: number): string[] => getState(i).selected;
    const noteFor = (i: number): string => getState(i).note;

    const anyNote = Object.values(answers).some((s) => s.note.trim().length > 0);
    const isShortcut = questions.length === 1 && questions[0].select === 'single' && questions[0].options.length > 0;
    const instantSubmit = isShortcut && !anyNote;
    const showSubmit = !instantSubmit;

    const buildPayload = (state: Record<number, QState>): string =>
        JSON.stringify({
            answers: questions.map((q, i) => {
                const s = state[i] ?? { selected: [], note: '' };
                const note = s.note.trim();
                return { questionId: q.id || String(i), selected: s.selected, ...(note ? { note } : {}) };
            }),
        });

    const submit = (state: Record<number, QState> = answers) => {
        if (!callId) return;
        const payload = buildPayload(state);
        setSubmitted(payload);
        agentGatewayService.answerHumanTool(callId, payload);
    };

    const pickSingle = (i: number, opt: string) => {
        const cur = getState(i);
        const next = cur.selected[0] === opt ? [] : [opt]; // click again to deselect (= none)
        const nextState = { ...answers, [i]: { ...cur, selected: next } };
        if (instantSubmit && next.length > 0) {
            submit(nextState);
            return;
        }
        setAnswers(nextState);
    };

    const toggleMulti = (i: number, opt: string) => {
        const cur = getState(i);
        const next = cur.selected.includes(opt)
            ? cur.selected.filter((o) => o !== opt)
            : [...cur.selected, opt];
        setAnswers({ ...answers, [i]: { ...cur, selected: next } });
    };

    const setNote = (i: number, note: string) => {
        setAnswers({ ...answers, [i]: { ...getState(i), note } });
    };

    return (
        <Box sx={outerSx}>
            {title && (
                <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 600 }}>{title}</Typography>
            )}

            <Stack spacing={2}>
                {questions.map((q, i) => (
                    <Box key={qid(i)}>
                        <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500 }}>{q.text}</Typography>

                        {q.options.length > 0 && q.select === 'multi' && (
                            <FormGroup>
                                {q.options.map((opt) => (
                                    <FormControlLabel
                                        key={opt}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={selectedFor(i).includes(opt)}
                                                onChange={() => toggleMulti(i, opt)}
                                            />
                                        }
                                        label={<Typography variant="body2">{opt}</Typography>}
                                    />
                                ))}
                            </FormGroup>
                        )}

                        {q.options.length > 0 && q.select === 'single' && (
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {q.options.map((opt) => (
                                    <Button
                                        key={opt}
                                        size="small"
                                        variant={selectedFor(i).includes(opt) ? 'contained' : 'outlined'}
                                        color="primary"
                                        onClick={() => pickSingle(i, opt)}
                                        sx={{color:theme.palette.text.secondary}}
                                    >
                                        {opt}
                                    </Button>
                                ))}
                            </Stack>
                        )}

                        <TextField
                            value={noteFor(i)}
                            onChange={(e) => setNote(i, e.target.value)}
                            placeholder={
                                q.options.length > 0
                                    ? 'Add a note, or something not listed (optional)'
                                    : 'Type your answer…'
                            }
                            size="small"
                            fullWidth
                            multiline
                            maxRows={3}
                            sx={{ mt: q.options.length > 0 ? 1 : 0.5 }}
                        />
                    </Box>
                ))}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                {showSubmit ? (
                    <Button size="small" variant="contained" color="primary" onClick={() => submit()}>
                        Submit
                    </Button>
                ) : (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Pick an option to answer
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};

export default AskUserPart;
